/**
 * Selection service - manages photo selection for batch operations
 * Core domain service for the download cart feature
 */

import { PhotoId } from './domain.js';
import { ISelectionStorage } from './ports.js';

/**
 * Interface for selection service
 * This is what adapters (like WebUI) depend on
 */
export interface ISelectionService {
  /**
   * Add a photo to selection
   */
  select(photoId: PhotoId): void;

  /**
   * Remove a photo from selection
   */
  deselect(photoId: PhotoId): void;

  /**
   * Toggle selection state of a photo
   */
  toggleSelection(photoId: PhotoId): void;

  /**
   * Check if a photo is selected
   */
  isSelected(photoId: PhotoId): boolean;

  /**
   * Get all selected photo IDs
   */
  getSelectedIds(): ReadonlySet<PhotoId>;

  /**
   * Get count of selected items
   */
  getSelectionCount(): number;

  /**
   * Clear all selections
   */
  clearSelection(): void;
}

/**
 * Implementation of selection service
 */
export class SelectionService implements ISelectionService {
  private selectedIds: Set<PhotoId>;
  private storage: ISelectionStorage;
  private saveTimeout?: ReturnType<typeof setTimeout>;

  constructor(storage: ISelectionStorage) {
    this.storage = storage;
    // Load persisted selections on initialization
    this.selectedIds = storage.loadSelection();
  }

  select(photoId: PhotoId): void {
    if (!this.selectedIds.has(photoId)) {
      this.selectedIds.add(photoId);
      this.scheduleSave();
    }
  }

  deselect(photoId: PhotoId): void {
    if (this.selectedIds.has(photoId)) {
      this.selectedIds.delete(photoId);
      this.scheduleSave();
    }
  }

  toggleSelection(photoId: PhotoId): void {
    if (this.selectedIds.has(photoId)) {
      this.selectedIds.delete(photoId);
    } else {
      this.selectedIds.add(photoId);
    }
    this.scheduleSave();
  }

  isSelected(photoId: PhotoId): boolean {
    return this.selectedIds.has(photoId);
  }

  getSelectedIds(): ReadonlySet<PhotoId> {
    return this.selectedIds;
  }

  getSelectionCount(): number {
    return this.selectedIds.size;
  }

  clearSelection(): void {
    this.selectedIds.clear();
    this.storage.clearSelection();
  }

  /**
   * Debounced save to localStorage (500ms)
   */
  private scheduleSave(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    this.saveTimeout = setTimeout(() => {
      this.storage.saveSelection(this.selectedIds);
    }, 500);
  }
}
